/**
 * Function-based access control for TBF MAP.
 * Roles are persona templates; permissions are granular functions/modules.
 */

export const PERMISSIONS = {
  DASHBOARD_VIEW: 'dashboard.view',

  REGISTRATION_REGION_ASSOCIATION: 'registration.region_association',
  REGISTRATION_REGIONAL_TEAM: 'registration.regional_team',
  REGISTRATION_REGIONAL_PLAYERS: 'registration.regional_players',
  REGISTRATION_CLUB: 'registration.club',
  REGISTRATION_PLAYER: 'registration.player',
  REGISTRATION_OFFICIAL: 'registration.official',
  REGISTRATION_COACH: 'registration.coach',

  TOURNAMENT_GAMES: 'tournament.games',
  TOURNAMENT_OFFICIALS: 'tournament.officials',
  TOURNAMENT_REGIONAL_TEAMS: 'tournament.regional_teams',
  TOURNAMENT_PLAYER_STATS: 'tournament.player_stats',

  LEAGUE_GAMES: 'league.games',
  LEAGUE_ORGANIZER: 'league.organizer',
  LEAGUE_OFFICIALS: 'league.officials',
  LEAGUE_COACHES: 'league.coaches',
  LEAGUE_TEAMS: 'league.teams',
  LEAGUE_PLAYERS: 'league.players',
  LEAGUE_STATISTICS: 'league.statistics',

  MATCH_LIVE: 'match.live',
  MATCH_UPDATES: 'match.updates',

  ADMIN_APPROVALS: 'admin.approvals',
  ADMIN_USERS: 'admin.users',
  ADMIN_REPORTS: 'admin.reports',
  ADMIN_EMAIL: 'admin.email',

  FINANCE_DASHBOARD: 'finance.dashboard',
  FINANCE_REGIONS: 'finance.regions',
  FINANCE_MEMBERSHIPS: 'finance.memberships',
};

export const PERMISSION_LABELS = {
  [PERMISSIONS.DASHBOARD_VIEW]: 'Dashboard',

  [PERMISSIONS.REGISTRATION_REGION_ASSOCIATION]: 'Region Association Registration',
  [PERMISSIONS.REGISTRATION_REGIONAL_TEAM]: 'Regional Team Registration',
  [PERMISSIONS.REGISTRATION_REGIONAL_PLAYERS]: 'Regional Team Players Registration',
  [PERMISSIONS.REGISTRATION_CLUB]: 'Club Registration',
  [PERMISSIONS.REGISTRATION_PLAYER]: 'Player Registration',
  [PERMISSIONS.REGISTRATION_OFFICIAL]: 'Official Registration',
  [PERMISSIONS.REGISTRATION_COACH]: 'Coach Registration',

  [PERMISSIONS.TOURNAMENT_GAMES]: 'Tournament Games Organizer',
  [PERMISSIONS.TOURNAMENT_OFFICIALS]: 'Tournament Officials',
  [PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS]: 'Regional Teams Profiles',
  [PERMISSIONS.TOURNAMENT_PLAYER_STATS]: 'Regional Player Statistics',

  [PERMISSIONS.LEAGUE_GAMES]: 'Games Management',
  [PERMISSIONS.LEAGUE_ORGANIZER]: 'League Organizer',
  [PERMISSIONS.LEAGUE_OFFICIALS]: 'League Officials',
  [PERMISSIONS.LEAGUE_COACHES]: 'League Coaches',
  [PERMISSIONS.LEAGUE_TEAMS]: 'Team Management',
  [PERMISSIONS.LEAGUE_PLAYERS]: 'Players Profiles',
  [PERMISSIONS.LEAGUE_STATISTICS]: 'Player Statistics',

  [PERMISSIONS.MATCH_LIVE]: 'Live Match Management',
  [PERMISSIONS.MATCH_UPDATES]: 'Matches Updates',

  [PERMISSIONS.ADMIN_APPROVALS]: 'Approvals',
  [PERMISSIONS.ADMIN_USERS]: 'User Management',
  [PERMISSIONS.ADMIN_REPORTS]: 'Reports',
  [PERMISSIONS.ADMIN_EMAIL]: 'Email Configuration',

  [PERMISSIONS.FINANCE_DASHBOARD]: 'Finance Dashboard',
  [PERMISSIONS.FINANCE_REGIONS]: 'Region Finance Profiles',
  [PERMISSIONS.FINANCE_MEMBERSHIPS]: 'Membership & License Records',
};

const ALL_PERMISSIONS = Object.values(PERMISSIONS);

export const ROLE_DEFINITIONS = {
  admin: {
    label: 'System Administrator',
    description: 'Full platform access, user management, and system configuration.',
    permissions: ALL_PERMISSIONS,
  },
  staff: {
    label: 'TBF Staff',
    description: 'National federation operations — registrations, leagues, and tournaments.',
    permissions: ALL_PERMISSIONS.filter(
      (key) => !key.startsWith('admin.')
    ),
  },
  region_admin: {
    label: 'Regional Leader',
    description: 'Manage one region — associations, regional teams, and regional players.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.REGISTRATION_REGION_ASSOCIATION,
      PERMISSIONS.REGISTRATION_REGIONAL_TEAM,
      PERMISSIONS.REGISTRATION_REGIONAL_PLAYERS,
      PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS,
      PERMISSIONS.TOURNAMENT_PLAYER_STATS,
    ],
  },
  team_manager: {
    label: 'Club Team Manager',
    description: 'Manage club teams, players, officials, and coaches for assigned clubs.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.REGISTRATION_REGIONAL_TEAM,
      PERMISSIONS.REGISTRATION_REGIONAL_PLAYERS,
      PERMISSIONS.REGISTRATION_CLUB,
      PERMISSIONS.REGISTRATION_PLAYER,
      PERMISSIONS.REGISTRATION_OFFICIAL,
      PERMISSIONS.REGISTRATION_COACH,
      PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS,
      PERMISSIONS.TOURNAMENT_PLAYER_STATS,
      PERMISSIONS.LEAGUE_COACHES,
      PERMISSIONS.LEAGUE_TEAMS,
      PERMISSIONS.LEAGUE_PLAYERS,
      PERMISSIONS.LEAGUE_STATISTICS,
    ],
  },
  official: {
    label: 'Match Official',
    description: 'Live match operations and fixture updates.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.MATCH_LIVE,
      PERMISSIONS.MATCH_UPDATES,
    ],
  },
  commissioner: {
    label: 'Commissioner',
    description: 'Oversee competitions, approvals, fixtures, and league compliance.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.ADMIN_APPROVALS,
      PERMISSIONS.ADMIN_REPORTS,
      PERMISSIONS.LEAGUE_GAMES,
      PERMISSIONS.LEAGUE_ORGANIZER,
      PERMISSIONS.LEAGUE_OFFICIALS,
      PERMISSIONS.TOURNAMENT_GAMES,
      PERMISSIONS.TOURNAMENT_OFFICIALS,
      PERMISSIONS.MATCH_LIVE,
      PERMISSIONS.MATCH_UPDATES,
      PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS,
      PERMISSIONS.TOURNAMENT_PLAYER_STATS,
    ],
  },
  government_official: {
    label: 'Government Official',
    description: 'Read-only oversight of reports, regional data, and statistics.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.ADMIN_REPORTS,
      PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS,
      PERMISSIONS.TOURNAMENT_PLAYER_STATS,
      PERMISSIONS.LEAGUE_STATISTICS,
      PERMISSIONS.LEAGUE_PLAYERS,
      PERMISSIONS.LEAGUE_TEAMS,
    ],
  },
  player: {
    label: 'Player',
    description: 'Self-service player registration and profile access.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.REGISTRATION_PLAYER,
    ],
  },
  finance_manager: {
    label: 'Finance Manager',
    description: 'Manage regional bank details, license membership payments, receipts, and financial records.',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.FINANCE_DASHBOARD,
      PERMISSIONS.FINANCE_REGIONS,
      PERMISSIONS.FINANCE_MEMBERSHIPS,
      PERMISSIONS.ADMIN_REPORTS,
    ],
  },
};

export const ROLE_OPTIONS = Object.entries(ROLE_DEFINITIONS).map(([value, config]) => ({
  value,
  label: config.label,
  description: config.description,
}));

/** Route path → required permission (exact match). */
export const ROUTE_PERMISSIONS = {
  '/admin-dashboard': PERMISSIONS.DASHBOARD_VIEW,
  '/region-association-registration': PERMISSIONS.REGISTRATION_REGION_ASSOCIATION,
  '/regional-team-registration': PERMISSIONS.REGISTRATION_REGIONAL_TEAM,
  '/regional-team-players-registration': PERMISSIONS.REGISTRATION_REGIONAL_PLAYERS,
  '/team-registration': PERMISSIONS.REGISTRATION_CLUB,
  '/player-registration': PERMISSIONS.REGISTRATION_PLAYER,
  '/official-registration': PERMISSIONS.REGISTRATION_OFFICIAL,
  '/coach-registration': PERMISSIONS.REGISTRATION_COACH,
  '/tournament-games-organizer': PERMISSIONS.TOURNAMENT_GAMES,
  '/tournament-officials': PERMISSIONS.TOURNAMENT_OFFICIALS,
  '/regional-teams-profiles': PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS,
  '/regional-team-player-statistics': PERMISSIONS.TOURNAMENT_PLAYER_STATS,
  '/games-management': PERMISSIONS.LEAGUE_GAMES,
  '/league-organizer': PERMISSIONS.LEAGUE_ORGANIZER,
  '/league-management': PERMISSIONS.LEAGUE_ORGANIZER,
  '/league-setup': PERMISSIONS.LEAGUE_ORGANIZER,
  '/league-officials': PERMISSIONS.LEAGUE_OFFICIALS,
  '/league-coaches': PERMISSIONS.LEAGUE_COACHES,
  '/team-management': PERMISSIONS.LEAGUE_TEAMS,
  '/team-profiles': PERMISSIONS.LEAGUE_TEAMS,
  '/players-profiles': PERMISSIONS.LEAGUE_PLAYERS,
  '/player-statistics': PERMISSIONS.LEAGUE_STATISTICS,
  '/match-management': PERMISSIONS.MATCH_LIVE,
  '/matches-updates': PERMISSIONS.MATCH_UPDATES,
  '/approvals': PERMISSIONS.ADMIN_APPROVALS,
  '/user-management': PERMISSIONS.ADMIN_USERS,
  '/reports': PERMISSIONS.ADMIN_REPORTS,
  '/email-configuration': PERMISSIONS.ADMIN_EMAIL,
  '/finance-dashboard': PERMISSIONS.FINANCE_DASHBOARD,
  '/region-finance': PERMISSIONS.FINANCE_REGIONS,
  '/membership-records': PERMISSIONS.FINANCE_MEMBERSHIPS,
  '/profile': PERMISSIONS.DASHBOARD_VIEW,
};

export const NAVIGATION_SECTIONS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    permission: PERMISSIONS.DASHBOARD_VIEW,
    items: [
      { label: 'Overview', path: '/admin-dashboard', icon: 'Home', permission: PERMISSIONS.DASHBOARD_VIEW },
    ],
  },
  {
    id: 'registrations',
    label: 'Registrations',
    icon: 'UserPlus',
    permission: PERMISSIONS.REGISTRATION_CLUB,
    items: [
      { label: 'Region Association', path: '/region-association-registration', icon: 'MapPin', permission: PERMISSIONS.REGISTRATION_REGION_ASSOCIATION },
      { label: 'Regional Team Registration', path: '/regional-team-registration', icon: 'Map', permission: PERMISSIONS.REGISTRATION_REGIONAL_TEAM },
      { label: 'Regional Team Players', path: '/regional-team-players-registration', icon: 'UsersRound', permission: PERMISSIONS.REGISTRATION_REGIONAL_PLAYERS },
      { label: 'Club Registration', path: '/team-registration', icon: 'Users', permission: PERMISSIONS.REGISTRATION_CLUB },
      { label: 'Player Registration', path: '/player-registration', icon: 'User', permission: PERMISSIONS.REGISTRATION_PLAYER },
      { label: 'Official Registration', path: '/official-registration', icon: 'UserCheck', permission: PERMISSIONS.REGISTRATION_OFFICIAL },
      { label: 'Coach Registration', path: '/coach-registration', icon: 'GraduationCap', permission: PERMISSIONS.REGISTRATION_COACH },
    ],
  },
  {
    id: 'tournaments',
    label: 'Tournament Management',
    icon: 'Medal',
    permission: PERMISSIONS.TOURNAMENT_GAMES,
    items: [
      { label: 'Games Organizer', path: '/tournament-games-organizer', icon: 'CalendarDays', permission: PERMISSIONS.TOURNAMENT_GAMES },
      { label: 'Tournament Officials', path: '/tournament-officials', icon: 'UserCheck', permission: PERMISSIONS.TOURNAMENT_OFFICIALS },
      { label: 'Regional Teams Profiles', path: '/regional-teams-profiles', icon: 'Shield', permission: PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS },
      { label: 'Regional Players Stats', path: '/regional-team-player-statistics', icon: 'BarChart3', permission: PERMISSIONS.TOURNAMENT_PLAYER_STATS },
    ],
  },
  {
    id: 'management',
    label: 'League Management',
    icon: 'Trophy',
    permission: PERMISSIONS.LEAGUE_GAMES,
    items: [
      { label: 'Games Management', path: '/games-management', icon: 'Calendar', permission: PERMISSIONS.LEAGUE_GAMES },
      { label: 'League Organizer', path: '/league-organizer', icon: 'Trophy', permission: PERMISSIONS.LEAGUE_ORGANIZER },
      { label: 'League Officials', path: '/league-officials', icon: 'UserCheck', permission: PERMISSIONS.LEAGUE_OFFICIALS },
      { label: 'League Coaches', path: '/league-coaches', icon: 'GraduationCap', permission: PERMISSIONS.LEAGUE_COACHES },
      { label: 'Team Management', path: '/team-management', icon: 'ClipboardList', permission: PERMISSIONS.LEAGUE_TEAMS },
      { label: 'Team Profiles', path: '/team-profiles', icon: 'Shield', permission: PERMISSIONS.LEAGUE_TEAMS },
      { label: 'Players Profiles', path: '/players-profiles', icon: 'Users', permission: PERMISSIONS.LEAGUE_PLAYERS },
      { label: 'Player Statistics', path: '/player-statistics', icon: 'BarChart3', permission: PERMISSIONS.LEAGUE_STATISTICS },
    ],
  },
  {
    id: 'operations',
    label: 'Match Operations',
    icon: 'Calendar',
    permission: PERMISSIONS.MATCH_LIVE,
    items: [
      { label: 'Live Match Management', path: '/match-management', icon: 'Activity', permission: PERMISSIONS.MATCH_LIVE },
      { label: 'Matches Updates', path: '/matches-updates', icon: 'ClipboardList', permission: PERMISSIONS.MATCH_UPDATES },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: 'Wallet',
    permission: PERMISSIONS.FINANCE_DASHBOARD,
    items: [
      { label: 'Finance Dashboard', path: '/finance-dashboard', icon: 'LayoutDashboard', permission: PERMISSIONS.FINANCE_DASHBOARD },
      { label: 'Region Finance Profiles', path: '/region-finance', icon: 'MapPin', permission: PERMISSIONS.FINANCE_REGIONS },
      { label: 'Membership Records', path: '/membership-records', icon: 'Receipt', permission: PERMISSIONS.FINANCE_MEMBERSHIPS },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: 'Shield',
    permission: PERMISSIONS.ADMIN_APPROVALS,
    items: [
      { label: 'Approvals', path: '/approvals', icon: 'ClipboardCheck', permission: PERMISSIONS.ADMIN_APPROVALS },
      { label: 'User Management', path: '/user-management', icon: 'Users', permission: PERMISSIONS.ADMIN_USERS },
      { label: 'Reports', path: '/reports', icon: 'FileBarChart', permission: PERMISSIONS.ADMIN_REPORTS },
      { label: 'Email Configuration', path: '/email-configuration', icon: 'Mail', permission: PERMISSIONS.ADMIN_EMAIL },
    ],
  },
];

export const getDefaultRouteForRole = (role) => {
  switch (role) {
    case 'team_manager':
      return '/team-registration';
    case 'player':
      return '/player-registration';
    case 'official':
      return '/match-management';
    case 'government_official':
      return '/reports';
    case 'commissioner':
      return '/approvals';
    case 'region_admin':
      return '/region-association-registration';
    case 'finance_manager':
      return '/finance-dashboard';
    default:
      return '/admin-dashboard';
  }
};
